import type {
  SharedBillingCheckoutStatus,
  SharedBillingDetails,
  SharedBillingInvoice,
  SharedBillingPlan,
  SharedBillingProductAdapter,
  SharedBillingSnapshot,
  SharedBillingSubscription,
} from "../../../../../../shared/frontend/billing/sharedBillingPageTypes";
import type { ApiClient } from "../api/client";
import { planLimitDetails } from "./m1memberPlanLimits";

const BILLING_CYCLE_YEARLY = "yearly";
const BILLING_CYCLE_MONTHLY = "monthly";
/** Einmaliger Einrichtungspreis, solange das Backend keinen Preis meldet. */
const DEFAULT_ONBOARDING_PRICE = 599;

type Translate = (key: string) => string;

interface WorkspacePlan {
  id: string;
  name: string;
  description?: string | null;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  details: string[];
  priceOnRequest: boolean;
  trialDays: number;
  disabled: boolean;
  isPerUnit: boolean;
  unitKey?: string | null;
  minQuantity: number;
  maxQuantity?: number | null;
  limits?: Record<string, number | null> | null;
}

interface WorkspaceSubscription {
  planId: string;
  planName: string;
  billingCycle: string;
  price: number;
  yearlyFee: number;
  currency: string;
  periodEnd?: string | null;
  paidThrough?: string | null;
  accessState: string;
  cancelAtPeriodEnd: boolean;
  canChange: boolean;
  canCancel: boolean;
  paymentState: string;
  feeExempt?: boolean;
  quantity: number;
  pendingChange?: { planName: string; quantity: number; effectiveAt: string } | null;
}

interface WorkspaceAddon {
  id: string;
  name: string;
  description?: string | null;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  includedInCurrentPlan: boolean;
  includedInPlanIds: string[];
  booked: boolean;
}

interface WorkspaceSnapshot {
  subject: {
    id: string;
    billingAvailable: boolean;
    notice?: {
      type: "info" | "warning";
      message: string;
      description?: string | null;
    } | null;
  };
  subscription: WorkspaceSubscription | null;
  plans: WorkspacePlan[];
  addons: WorkspaceAddon[];
  yearlyDiscountPercent: number;
  billingDetails: SharedBillingDetails;
  unitsInUse: number;
  units?: { id: string; label: string; locked: boolean; lockScheduledAt?: string | null }[];
  capabilities?: {
    onboardingOption?: {
      id: string;
      title: string;
      description: string;
      priceText?: string;
      price?: number;
    };
  };
}

interface InvoiceResponse {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  servicePeriodStart?: string | null;
  servicePeriodEnd?: string | null;
  grossAmount: number;
  currency: string;
  status: string;
  downloadable: boolean;
  documentType?: string;
}

interface PlanChangeQuoteResponse {
  changeId: string;
  chargeAmount: number;
  creditAmount: number;
  refundAmount: number;
  currency: string;
  effectiveAt: string;
  requiresCheckout: boolean;
  providerSupported: boolean;
  availablePaymentInterfaces: never[];
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  taxRatePercent: number;
  taxNoteKey?: string | null;
  lines?: { key: string; name: string; quantity: number; unitNetAmount: number; netAmount: number; oneTime: boolean }[];
  recurringGrossAmount?: number;
  nextPaymentAt?: string;
  settlementNotice?: string | null;
}

interface CheckoutResponse {
  checkoutUrl?: string | null;
  applied?: boolean;
}

interface Options {
  api: ApiClient;
  tr: Translate;
}

/**
 * Produkt-Adapter für `SharedBillingPage`: ausschließlich Endpunktverdrahtung. Beträge, Steuer und
 * Zahlungsweg entstehen im Backend über `shared/backend/Billing`.
 */
export function createM1MemberBillingAdapter({ api, tr }: Options): SharedBillingProductAdapter {
  const toPlan = (plan: WorkspacePlan): SharedBillingPlan => {
    const mapped: SharedBillingPlan = {
      id: plan.id,
      name: plan.name,
      description: plan.description ?? undefined,
      monthlyPrice: plan.monthlyPrice,
      yearlyPrice: plan.yearlyPrice,
      currency: plan.currency,
      details: plan.details,
      priceOnRequest: plan.priceOnRequest,
      trialDays: plan.trialDays,
      disabled: plan.disabled,
      isPerUnit: plan.isPerUnit,
      unitKey: plan.unitKey ?? undefined,
      minQuantity: plan.minQuantity,
      maxQuantity: plan.maxQuantity ?? undefined,
      limits: plan.limits ?? undefined,
    };
    // Standorte, KI-Kontingent und gesetzte Mengenlimits stammen aus dem Katalog und stehen vorn.
    return { ...mapped, details: [...planLimitDetails(mapped, tr), ...plan.details.map((detail) => tr(detail))] };
  };

  const toSubscription = (subscription: WorkspaceSubscription | null): SharedBillingSubscription | null => {
    if (!subscription) return null;
    return {
      planId: subscription.planId,
      planName: subscription.planName,
      billingCycle: subscription.billingCycle === BILLING_CYCLE_YEARLY ? BILLING_CYCLE_YEARLY : BILLING_CYCLE_MONTHLY,
      price: subscription.price,
      yearlyFee: subscription.yearlyFee,
      currency: subscription.currency,
      periodEnd: subscription.periodEnd ?? undefined,
      paidThrough: subscription.paidThrough ?? undefined,
      accessState: subscription.accessState as SharedBillingSubscription["accessState"],
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      canChange: subscription.canChange,
      canCancel: subscription.canCancel,
      paymentState: subscription.paymentState as SharedBillingSubscription["paymentState"],
      feeExempt: subscription.feeExempt ? { enabled: true, label: tr("Fee exempt") } : undefined,
      quantity: subscription.quantity,
      pendingChange: subscription.pendingChange
        ? {
            planName: `${subscription.pendingChange.planName} · ${tr("{count} locations").replace("{count}", String(subscription.pendingChange.quantity))}`,
            effectiveAt: subscription.pendingChange.effectiveAt,
          }
        : undefined,
    };
  };

  const toInvoice = (invoice: InvoiceResponse): SharedBillingInvoice => ({
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate,
    servicePeriodStart: invoice.servicePeriodStart ?? undefined,
    servicePeriodEnd: invoice.servicePeriodEnd ?? undefined,
    grossAmount: invoice.grossAmount,
    currency: invoice.currency,
    status: invoice.status,
    downloadable: invoice.downloadable,
    documentType: invoice.documentType,
  });

  return {
    load: async (): Promise<SharedBillingSnapshot> => {
      const [data, invoices] = await Promise.all([
        api.get<WorkspaceSnapshot>("/api/v1/billing/workspace"),
        api.get<InvoiceResponse[]>("/api/v1/billing/invoices"),
      ]);

      const currentPlanId = data.subscription?.planId ?? null;

      return {
        subject: {
          id: data.subject.id,
          billingAvailable: data.subject.billingAvailable,
          notice: data.subject.notice
            ? {
                type: data.subject.notice.type,
                message: data.subject.notice.message,
                description: data.subject.notice.description ?? undefined,
              }
            : undefined,
        },
        subscription: toSubscription(data.subscription),
        plans: (data.plans ?? []).map(toPlan),
        unitsInUse: data.unitsInUse,
        units: (data.units ?? []).map((unit) => ({
          id: unit.id,
          label: unit.label,
          locked: unit.locked,
          lockScheduledAt: unit.lockScheduledAt ?? undefined,
        })),
        yearlyDiscountPercent: data.yearlyDiscountPercent,
        billingDetails: data.billingDetails ?? {},
        invoices: (Array.isArray(invoices) ? invoices : []).map(toInvoice),
        capabilities: {
          // Zubuchbare Bausteine erscheinen als buchbare Leistungen. In einem Tarif enthaltene
          // Bausteine werden als „gebucht" ohne Kaufschaltfläche gezeigt, statt sie zu verbergen —
          // sonst wäre nicht erkennbar, was der Tarif bereits abdeckt.
          specialServices: [
            ...(data.addons ?? []).map((addon) => ({
              id: addon.id,
              title: addon.name,
              description: addon.description ?? undefined,
              price: addon.monthlyPrice,
              yearlyPrice: addon.yearlyPrice,
              currency: addon.currency,
              status: addon.includedInCurrentPlan || addon.booked ? ("booked" as const) : ("available" as const),
              purchasable: !addon.includedInCurrentPlan && !addon.booked && Boolean(currentPlanId),
              includedInPlanIds: addon.includedInPlanIds,
            })),
            ...(data.capabilities?.onboardingOption
              ? [
                  {
                    id: data.capabilities.onboardingOption.id,
                    title: data.capabilities.onboardingOption.title,
                    description: data.capabilities.onboardingOption.description,
                    price: data.capabilities.onboardingOption.price ?? DEFAULT_ONBOARDING_PRICE,
                    yearlyPrice: data.capabilities.onboardingOption.price ?? DEFAULT_ONBOARDING_PRICE,
                    currency: "EUR",
                    status: "available" as const,
                    purchasable: true,
                    includedInPlanIds: [],
                    isOneTime: true,
                    priceText: data.capabilities.onboardingOption.priceText,
                  },
                ]
              : []),
          ],
          onboardingOption: data.capabilities?.onboardingOption,
        },
      };
    },

    saveBillingDetails: async (input) => api.put<SharedBillingDetails>("/api/v1/billing/details", input),

    quotePlanChange: async ({ planId, billingCycle, addonIds, quantity }) => {
      const quote = await api.post<PlanChangeQuoteResponse>("/api/v1/billing/plan-change/quote", {
        planId,
        billingCycle,
        addonIds,
        quantity,
      });
      return {
        changeId: quote.changeId,
        chargeAmount: quote.chargeAmount,
        creditAmount: quote.creditAmount,
        refundAmount: quote.refundAmount,
        currency: quote.currency,
        effectiveAt: quote.effectiveAt,
        requiresCheckout: quote.requiresCheckout,
        providerSupported: quote.providerSupported,
        availablePaymentInterfaces: quote.availablePaymentInterfaces ?? [],
        netAmount: quote.netAmount,
        taxAmount: quote.taxAmount,
        grossAmount: quote.grossAmount,
        taxRatePercent: quote.taxRatePercent,
        taxNoteKey: quote.taxNoteKey ?? undefined,
        recurringGrossAmount: quote.recurringGrossAmount,
        nextPaymentAt: quote.nextPaymentAt,
        settlementNotice: quote.settlementNotice ?? undefined,
        lines: (quote.lines ?? []).map((line) => ({
          key: line.key,
          label: line.name,
          quantity: line.quantity,
          unitNetAmount: line.unitNetAmount,
          netAmount: line.netAmount,
          oneTime: line.oneTime,
        })),
      };
    },

    startPlanCheckout: async ({ changeId, planId, billingCycle, addonIds, quantity, lockUnitIds, paymentInterface, successUrl, cancelUrl }) => {
      const result = await api.post<CheckoutResponse>("/api/v1/billing/plan-change/checkout", {
        changeId,
        planId,
        billingCycle,
        addonIds,
        quantity,
        lockStudioIds: lockUnitIds,
        provider: paymentInterface?.provider,
        paymentMethod: paymentInterface?.paymentMethod,
        successUrl,
        cancelUrl,
      });
      return { checkoutUrl: result.checkoutUrl ?? undefined, applied: result.applied };
    },

    getPlanCheckoutStatus: async ({ changeId }): Promise<SharedBillingCheckoutStatus> => {
      const result = await api.get<{ status: string }>(
        `/api/v1/billing/plan-change/status?changeId=${encodeURIComponent(changeId)}`
      );
      return (result?.status ?? "applied") as SharedBillingCheckoutStatus;
    },

    cancelSubscription: async () => {
      await api.post("/api/v1/billing/cancel", {});
    },

    reactivateSubscription: async () => {
      await api.post("/api/v1/billing/reactivate", {});
    },

    purchaseSpecialService: async (serviceId) => {
      const result = await api.post<CheckoutResponse>("/api/v1/billing/addons", { addonId: serviceId });
      return { checkoutUrl: result.checkoutUrl ?? undefined, applied: result.applied };
    },

    downloadInvoice: async (invoiceId) =>
      api.get<Blob>(`/api/v1/billing/invoices/${encodeURIComponent(invoiceId)}/document`, {
        // Der Beleg ist ein PDF, kein JSON.
        responseType: "blob",
      } as never),

    sendPlanInquiry: async (inquiry) => {
      await api.post("/api/v1/billing/inquiry", inquiry);
    },
  };
}
